import { Component, Input, OnInit } from '@angular/core';
import {
    Attribute,
    AttributeType,
    Brand,
    Category,
    CheckboxTreeNode,
    Country,
    Filters,
    Promotion,
    Retailer,
    RetailerType,
    SubBrand,
    Supplier,
} from '@emi/shared/models';
import {
    CheckboxState,
    DataExtractionState,
    getCheckboxState,
    getFilterCategoryState,
    getSelectedFilters,
} from '@emi/shared/state/filters';
import { select, Store } from '@ngrx/store';

@Component({
    selector: 'via-filter-selections',
    templateUrl: './filter-selections.component.html',
    styleUrls: ['./filter-selections.component.scss'],
})
export class FilterSelectionsComponent implements OnInit {
    @Input()
    public currentTab: number;

    @Input()
    public selections: Filters;
    public selectedCategories: Category[];
    public temporarySelections: Filters;
    public categories: Category[];

    constructor(private readonly store: Store<DataExtractionState>, private readonly checkboxStore: Store<CheckboxState>) {}

    public ngOnInit(): void {
        this.store.pipe(select(getSelectedFilters)).subscribe((x) => {
            this.selections = x;
            this.selectedCategories = this.getSelectedCategories(x.categories);
        });

        this.store.pipe(select(getFilterCategoryState)).subscribe((x) => {
            this.categories = x.categories;
        });

        this.checkboxStore.pipe(select(getCheckboxState)).subscribe((x) => {
            this.temporarySelections = new Filters();
            this.getTemporarySelections(x.checkboxes);
        });
    }

    public selectionsMade(): boolean {
        if (this.selections.dateRange) {
            return true;
        }

        for (const [key, val] of Object.entries(this.selections)) {
            if (val && val.length > 0) {
                return true;
            }
        }

        for (const [key, val] of Object.entries(this.temporarySelections)) {
            if (val && val.length > 0) {
                return true;
            }
        }

        return false;
    }

    public getSelectedCategories(categoryTree: Array<Category>, selectedCategories: Array<Category> = []): Array<Category> {
        for (const category of categoryTree) {
            if (!category.childCategories || category.childCategories.length === 0) {
                selectedCategories.push(category);
            } else {
                this.getSelectedCategories(category.childCategories, selectedCategories).sort((a, b) =>
                    a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1
                );
            }
        }
        return selectedCategories;
    }

    public getTemporarySelections(checkboxes: CheckboxTreeNode[]) {
        if (checkboxes.length === 0) return;

        switch (this.currentTab) {
            case 1:
                return checkboxes.map((country) => this.temporarySelections.countries.push(new Country(country.id, country.label,'','','')));
            case 2:
                let checkboxIds = checkboxes?.map((category) => `${category.id}:${category.industryCode}`);
                (this.getCategoryChildren(this.categories, checkboxIds) ?? []).map((child) =>
                    this.temporarySelections.categories.push(this.addCategoryBreadcrumbs(child))
                );
                return (this.temporarySelections.categories = this.getSelectedCategories(this.temporarySelections.categories));
            case 3:
                return checkboxes.map((supplier) => this.temporarySelections.suppliers.push(new Supplier(supplier.id, supplier.label)));
            case 4:
                return checkboxes.map((brand) => {
                const subBrands = (brand.children ?? []).map(
                    (subBrand) => new SubBrand(subBrand.id, subBrand.label)
                );
                return this.temporarySelections.brands.push(
                    new Brand(brand.id, brand.label, subBrands)
                );
                });
            case 5:
                return checkboxes.map((attributeType) =>
                    this.temporarySelections.attributeTypes.push(new AttributeType(attributeType.id, attributeType.label))
                );
            case 6:
                return checkboxes.map((attribute) =>
                    this.temporarySelections.attributes.push(new Attribute(attribute.id, attribute.label))
                );
            case 7:
                return checkboxes.map((retailerType) =>
                    this.temporarySelections.retailerTypes.push(new RetailerType(retailerType.id, retailerType.label))
                );
            case 8:
                return checkboxes.map((retailer) => this.temporarySelections.retailers.push(new Retailer(retailer.id, retailer.label)));
            case 10:
                return checkboxes.map((promotion) =>
                    this.temporarySelections.promotions.push(new Promotion(promotion.id, promotion.label))
                );
            default:
                return;
        }
    }

    private getCategoryChildren(categories: Category[], selectedCategoryIds: string[]): Category[] | null {
        const relevantCategories = [];
        for (const category of categories) {
            const parentChildKey = `${category.id}:${category.industryCode}`;
            if (selectedCategoryIds.includes(parentChildKey)) {
                relevantCategories.push(category);
            } else if (category.childCategories) {
                const relevantChildren = this.getCategoryChildren(category.childCategories, selectedCategoryIds);
                if (relevantChildren) {
                    let newCategory = { ...category, childCategories: relevantChildren };
                    relevantCategories.push(newCategory);
                }
            }
        }
        return relevantCategories.length > 0 ? relevantCategories : null;
    }

    private addCategoryBreadcrumbs(category: Category, breadcrumb?: string): Category {
        if (!category.childCategories || category.childCategories.length === 0) {
            return new Category(category.id, category.name, breadcrumb, undefined, undefined, category.industryCode, category.parentId);
        }
        const childCategories = [];
        for (const child of category.childCategories) {
            if (breadcrumb) {
                childCategories.push(this.addCategoryBreadcrumbs(child, `${breadcrumb} / ${category.name}`));
            } else {
                childCategories.push(this.addCategoryBreadcrumbs(child, category.name));
            }
        }
        return new Category(category.id, category.name, breadcrumb, childCategories, undefined, category.industryCode, category.parentId);
    }
}
